<template>
  <div>
    <v-data-table
      :headers="headers"
      :items="data"
      :loading="loading"
      hide-default-footer
      :options.sync="options"
      :server-items-length="24"
      :items-per-page="24"
      class="elevation-1 text-overflow my-10"
      @click:row="previewItem"
    >
      <!-- style="cursor:pointer" -->
      <template v-slot:top>
        <v-toolbar flat height="70">
          <v-toolbar-title class="headline" :data-cy="title">
            <span>
              {{ title }}
            </span>
            <v-btn
              v-if="canUseKnowledgeBaseBtn"
              @click="handleHelp"
              icon
              color="primary"
            >
              <v-icon>mdi-help-circle</v-icon>
            </v-btn>
          </v-toolbar-title>
          <v-spacer />
          <v-form @submit.prevent="submitSearch" v-if="showSearch">
            <v-text-field
              v-model="query.search"
              :label="$t('components.lists.builderList.search')"
              single-line
              hide-details
              color="primary"
              @click:append-outer="submitSearch"
              append-outer-icon="mdi-magnify"
            />
          </v-form>
          <v-spacer />
          <slot name="select-headers"></slot>
          <slot name="boat-import"></slot>

          <licence-tooltip
            v-if="canUserAddDelete && showAddNew"
            :feature="licenceFeatureBasedOnRoute"
            :disabled="licenceFeatureBasedOnRoute.disabled"
            :enabled="licenceFeatureBasedOnRoute.enabled"
          >
            <template v-slot:action="{ attrs, disabled, enabled }">
              <v-btn
                text
                color="primary"
                data-cy="builderAddNewBtn"
                @click="newItem()"
                v-bind="attrs"
                :disabled="disabled"
              >
                <v-icon small class="mr-1" v-if="enabled">
                  mdi-star-outline </v-icon
                >{{ $t("components.lists.builderList.addNew") }}
              </v-btn>
            </template>
          </licence-tooltip>
        </v-toolbar>
      </template>
      <template v-slot:item.is_boat_specific="{ item }">
        <v-icon class="mx-2" v-if="item.is_boat_specific">mdi-check</v-icon>
      </template>

      <!-- <template v-slot:item.model.brand.name="{ item }">
        <CopyContent :text="item.model.brand.name"> </CopyContent>
      </template> -->
      <template v-slot:item.vendors="{ item }">
        <v-chip
          v-bind:key="vendor.uuid"
          v-for="vendor in item.vendors"
          class="mx-1"
        >
          <span>{{ vendor.name }}</span>
        </v-chip>
      </template>

      <template v-slot:item.parent_relation="{ item }">
        <v-chip class="mx-1" v-if="item.parent_relation !== null">
          <span>{{ $t("components.lists.builderList.affiliate") }}</span>
        </v-chip>
        <v-chip class="mx-1" v-else>
          <span>{{ $t("components.lists.builderList.master") }}</span>
        </v-chip>
      </template>

      <template v-slot:item.domains="{ item }">
        <v-chip v-bind:key="domain" v-for="domain in item.domains" class="mx-1">
          <span>{{ domain.toLowerCase() }}</span>
        </v-chip>
      </template>

      <template v-slot:item.country="{ item }">
        <span>{{ prettyCountryName(item.country) }}</span>
      </template>

      <template v-slot:item.licence_type="{ item }">
        <span class="text--capitalize">{{ item.licence_type }}</span>
      </template>

      <template v-slot:item.billing_info="{ item }">
        <span
          v-if="
            item.billing_info !== null &&
            item.billing_info.subscription !== null
          "
        >
          <span
            class="text--capitalize"
            v-if="
              item.billing_info.subscription.status === 'ACTIVATION_REQUIRED'
            "
          >
            {{ $t("components.lists.builderList.activationRequired") }}</span
          >
          <span
            class="text--capitalize"
            v-if="item.billing_info.subscription.status === 'ACTIVE'"
          >
            {{ $t("components.lists.builderList.active") }}</span
          >
          <span
            class="text--capitalize"
            v-if="item.billing_info.subscription.status === 'EXPIRED'"
          >
            {{ $t("components.lists.builderList.expired") }}</span
          >
          <span
            class="text--capitalize"
            v-if="item.billing_info.subscription.status === 'INACTIVE'"
          >
            {{ $t("components.lists.builderList.inactive") }}</span
          >
        </span>
      </template>

      <template v-slot:item.ownership_status="{ item }">
        <span
          class="text--capitalize"
          v-if="item.ownership_status === 'NO_USER'"
          >{{ $t("components.lists.builderList.noUser") }}</span
        >
        <span
          class="text--capitalize"
          v-if="item.ownership_status === 'CUSTOMER'"
          >{{ $t("components.lists.builderList.customer") }}</span
        >
        <span
          class="text--capitalize"
          v-if="item.ownership_status === 'DEALER'"
          >{{ $t("components.lists.builderList.dealer") }}</span
        >
        <span
          class="text--capitalize"
          v-if="item.ownership_status === 'INSTALLER'"
          >{{ $t("components.lists.builderList.installer") }}</span
        >
        <span
          class="text--capitalize"
          v-if="item.ownership_status === 'PENDING_TRANSFER'"
          >{{ $t("components.lists.builderList.pendingTransfer") }}</span
        >
      </template>

      <template v-slot:item.categories="{ item }">
        <div v-if="item.categories != null">
          <v-chip
            :key="index"
            v-for="(category, index) in getCategories(item.categories)"
            class="mx-1"
          >
            <span>{{ category }}</span>
          </v-chip>
        </div>
      </template>
      <template v-slot:item.brands="{ item }">
        <v-chip
          v-bind:key="brand.uuid"
          v-for="brand in item.brands"
          class="ma-1"
        >
          <span>{{ brand.name }}</span>
        </v-chip>
      </template>
      <template v-slot:item.ce_number="{ item }">
        <span v-copy>{{ item.ce_number }}</span>
      </template>
      <template v-slot:item.licence="{ item }">
        <v-icon :color="item.licence.color">{{ item.licence.icon }}</v-icon>
      </template>
      <template v-slot:item.slot_number="{ item }">
        <span v-copy>{{ item.slot_number }}</span>
      </template>
      <template v-slot:item.device_serial="{ item }">
        <span v-copy>{{ item.device_serial }}</span>
      </template>
      <template v-slot:item.prettified_categories="{ item }">
        <v-chip
          v-bind:key="index"
          v-for="(category, index) in item.prettified_categories"
          class="mx-1 ma-1"
        >
          <v-icon class="mr-2" v-if="category.icon">{{ category.icon }}</v-icon>
          <span :class="{ 'text-capitalize': !!category.icon }">{{
            category.category
          }}</span>
        </v-chip>
      </template>
      <template v-slot:item.action="{ item }">
        <div
          style="height: 40px; width: 120px"
          class="pt-2 float-right"
          onclick="event.stopPropagation()"
        >
          <v-icon
            v-if="isItemEditable(item)"
            class="mx-2 edit--hover"
            @click="editItem(item)"
            >mdi-pencil
          </v-icon>
          <v-icon
            class="mx-2 delete--hover"
            v-if="canUserDelete && canUserAddDelete && isItemDeletable(item)"
            @click="deleteItem(item)"
            >mdi-delete
          </v-icon>
          <!-- <v-icon small class="mx-2 preview--hover" @click="previewItem(item)"
        >mdi-file-find</v-icon
      > -->
        </div>
      </template>
      <template v-slot:item.users="{ item }">
        <span v-if="item.users">{{ item.users.length }}</span>
      </template>
    </v-data-table>
    <v-pagination
      v-if="showPagination"
      v-model="currentPage"
      :length="numberOfPages"
      :total-visible="7"
      color="primary"
    />
    <help-dialog
      :dialog="helpDialog"
      @close="closeHelpDialog"
      v-if="helpDialog.active"
    ></help-dialog>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import HelpDialog from "@/components/dialogs/HelpDialog";
import constraints from "@/utils/constraints";
import constants from "@/utils/constants";
import LicenceTooltip from "@/components/general/LicenceTooltip";
export default {
  // components: {
  //   CopyContent,
  // },
  components: {
    HelpDialog,
    LicenceTooltip,
  },

  props: {
    title: String,
    headers: Array,
    data: Array,
    sort: String,
    loading: Boolean,
    response: Object,
    showAddNew: {
      type: Boolean,
      default: true,
    },
    canUserDelete: {
      type: Boolean,
      default: true,
    },
    showPagination: {
      type: Boolean,
      default: true,
    },
    showSearch: {
      type: Boolean,
      default: true,
    },
  },

  data: () => ({
    helpDialog: { location: null, active: false },
    options: { sortBy: [], sortDesc: [], page: [], itemsPerPage: 24 },
    query: {
      page: 1,
      search: "",
      ordering: "",
    },
  }),

  created() {
    this.query.search = this.$route.query.search || "";
    this.query.page = Number(this.$route.query.page) || 1;
    this.query.ordering = this.$route.query.ordering || "";
  },

  watch: {
    options: {
      handler(value) {
        const { sortBy, sortDesc, page, itemsPerPage } = this.options;
        if (sortBy.length !== 0 && sortDesc.length !== 0) {
          const header = this.headers.find(
            (header) => header.value === sortBy[0]
          );
          if (header == null) return;

          this.query = {
            ...this.query,
            ordering: `${sortDesc[0] === true ? "-" : ""}${
              header.orderingName
            }`,
          };
          this.$emit("sort");
        }
      },
      deep: true,
    },
  },

  computed: {
    canUseKnowledgeBaseBtn() {
      return (
        this.$route.name === "dealers" &&
        constraints.canUseKnowledgeBaseBtn("account")
      );
    },
    ...mapGetters([
      "getMe",
      "createNewPartsHandler",
      "createNewDocumentsHandler",
      "getDefaultTemplate",
    ]),

    licenceFeatureBasedOnRoute() {
      if (this.$route.name === "documents") {
        return this.createNewDocumentsHandler;
      }

      if (this.$route.name === "items") {
        return this.createNewPartsHandler;
      }

      return this.getDefaultTemplate;
    },

    canUserAddDelete() {
      if (this.$route.name === "boats") {
        return this.getMe.menu_access.boats.write_groups.length !== 0;
      }
      return true;
    },
    formTitle() {
      return this.title.substring(0, this.title.length - 1);
    },
    numberOfPages() {
      if (!this.response) {
        return 0;
      }
      return parseInt(Math.ceil(this.response.count / 24));
    },
    currentPage: {
      get: function () {
        return this.query.page;
      },
      set: function (value) {
        this.query = {
          ...this.query,
          page: value,
        };
        this.$emit("reload");
      },
    },
  },
  methods: {
    prettyCountryName(country) {
      const countries = constants.countries();
      const foundCountry = countries.find((element) => {
        return element.value === country || element.text === country;
      });

      if (typeof foundCountry === "undefined") {
        return;
      }

      return foundCountry.text;
    },
    isItemEditable(item) {
      if (this.$route.name === "items" || this.$route.name === "documents") {
        return item.is_editable;
      }

      return true;
    },
    isItemDeletable(item) {
      if (this.$route.name === "items" || this.$route.name === "documents") {
        return item.is_removable;
      }

      return true;
    },
    closeHelpDialog(event) {
      if (event === "close") {
        this.helpDialog.active = false;
      }
    },
    handleHelp(event) {
      this.helpDialog.active = true;
      this.helpDialog.location =
        this.$route.name === "dealers" ? "account" : "";
    },
    getCategories(categories) {
      const splitedCategories =
        categories.length > 5
          ? categories.slice(0, 5).concat("...")
          : categories;
      return splitedCategories;
    },
    submitSearch(event) {
      this.query.page = 1;
      this.$emit("reload");
      return false;
    },
    setPage(page) {
      //NOTE: This function is called from the outside
      this.query.page = page;
    },
    editItem(item) {
      this.$emit("edit", item);
    },
    deleteItem(item) {
      this.$emit("delete", item);
    },
    previewItem(item) {
      this.$emit("details", item);
    },
    newItem() {
      this.$emit("new", {});
    },
  },
};
</script>

<style lang="scss">
.delete--hover {
  &:hover {
    color: red;
  }
}

.preview--hover {
  &:hover {
    color: --secondary;
  }
}

.edit--hover {
  &:hover {
    color: --primary;
  }
}
.copy--hover {
  &:hover {
    color: --primary;
  }
}
.text--capitalize {
  text-transform: capitalize;
}
</style>
